import { Injectable, NgZone, inject, SecurityContext } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import {BehaviorSubject, Observable, of, Subject, throwError} from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LanguageDTO } from '@signature-it/ngx-generic/models';
import { ILanguage } from "@signature-it/ngx-generic/interfaces";
import { RestUrlsConfig, GenericConfig } from '@signature-it/ngx-generic/config';
import { FakeloaderComponent } from '@signature-it/ngx-generic/components/fakeloader';
import {DomSanitizer} from '@angular/platform-browser';

declare let $: any;
declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    languageSelected$ = new BehaviorSubject<ILanguage>(null);
    sanitizer: DomSanitizer;
    private mapRTL = ['he', 'ar'];
    private static readonly DIR_RTL = 'rtl';
    private static readonly DIR_LTR = 'ltr';
    langList: ILanguage[];
    currLangName: string;
    public currLang: ILanguage;
    protected langListSub = new BehaviorSubject<Array<ILanguage>>(null);
    public langList$: any;
	wcLangCode: string;

    constructor(protected http: HttpClient,
                protected restUrlsConf: RestUrlsConfig,
                protected translate: TranslateService,
                protected cookie: CookieService,
                protected router: Router,
                protected ngZone: NgZone) {
        this.langList$ = this.langListSub.asObservable();
        this.sanitizer = inject(DomSanitizer);
    }

    get Langlist(): ILanguage[] { return this.langList; }
    get CurrLangName() { return this.currLangName; }
    get CurrLang(): ILanguage { return this.currLang }

    private _chewLangCode(lang: ILanguage | string): string {
        let langCode;
        switch (typeof lang) {
            case "object":
                langCode = (lang instanceof LanguageDTO) ? lang.code : "";
                break;
            case "string":
                langCode = (typeof lang !== "undefined" && lang) ? lang : "";
                break;
        }
        return (langCode.length) ? langCode : this.getDefaultLanguage();
    }

    private subDomainRegex() {
        const codes = this.getStoredLangCodes().join('|');
        return new RegExp(`^(${codes})\\.`);
    }

    getAllLanguages(): Observable<ILanguage[]> {
        return this.http.get<ILanguage[]>(`${this.restUrlsConf.appURL}/public/language/all.json`)
            .pipe(map((res) => LanguageDTO.createFromArray(res)), catchError( (error: any) => throwError(error)));
    }

    getLanguageByCode(code: string): Observable<ILanguage> {
        return this.http.get<ILanguage>(`${this.restUrlsConf.appURL}/public/language/${code}.json`)
            .pipe(map((res) =>  new LanguageDTO(res)), catchError( (error: any) => throwError(error)));
    }

    /**
     * Current client's selected language of app
     * @returns ILanguage
     */
    getCurrentLanguage(): ILanguage {
        let lang;
        // View specific page ONLY using lang from query
        let langCodeFromQuery = this.getLangCodeFromQuery();
        let langCode = this.wcLangCode || langCodeFromQuery || this.cookie.get('lang') || this.getDefaultLanguage() || 'en';
        let langObj = {code: langCode, is_current: langCode == this.cookie.get('lang') || !!this.wcLangCode};
        let langList = this.getStoredLangList() || [langObj];
        lang = _.find(langList, langObj);
        return new LanguageDTO(lang);
    }

    getCurrentLanguage$(): Observable<ILanguage> {
        const lang = this.getCurrentLanguage();
        return lang.code !== "" ? of(lang) : this.languageSelected$;
    }

    /**
     * Current server's default language of app
     * @param Array<ILanguage> langList
     * @returns ILanguage
     */
    findCurrent(langList: Array<ILanguage>, langFromQuery?: string): ILanguage|null {
        if(GenericConfig.isMultiLanguages) {
            let langCode = langFromQuery || this.langFromURL(langList) || this.getDefaultLanguage();
            let langObj = {code: langCode};
            let lang = _.find(langList, langObj);
            if(lang) {
                return new LanguageDTO(lang);
            }
        } else {
            let lang = langList.find(lang => lang.code == langFromQuery);
            if (lang) {
                return lang;
            }

            for (let lang of langList) {
                if (lang.is_current) return lang;
            }
        }
        return null;
    }

    findByLangCode(langList: Array<ILanguage>, code: string): ILanguage|null {
        for (let lang of langList) {
            if (code === lang.code) return lang;
        }
        return null;
    }

    langFromURL(langList: Array<ILanguage>): string {
        // domain per lang
        const langByDomain = _.filter(langList, (lang: ILanguage) => location.host == lang.domain);
        if(langByDomain.length) {
            return langByDomain[0].code;
        }
        // sub-domain for each lang
        if (GenericConfig.useLangSubDomain) {
            // This code doesn't work in cases you don't have langList set in cookie yet
            const r = this.subDomainRegex();
            return location.hostname.match(r)[1];
        }
        // sub-directory for each lang
        return this.langFromSubDir(langList);
    }

    langFromSubDir(langList): string {
        const langCode = location.href.match(/^.{8}[^\/]*\/([^\/]*)/)[1];
        let lang = _.find(langList, {code: langCode}) ?? this.getDefaultLanguage();
        return lang.code;
    }

    getDefaultLanguage() {
        return GenericConfig.defaultLang;
    }

    updateCurrentLanguage(langCode: string) {
        let langList = _.map(this.getStoredLangList(), (sLang: ILanguage) => {
            return _.assign(sLang, {is_current: sLang.code == langCode});
        });
        this.storeLangList(langList);
    }

    isRTL(lang: ILanguage | string): boolean {
        let langCode = this._chewLangCode(lang);
        return _.includes(this.mapRTL, langCode);
    }

    isCurrentRTL(): boolean {
        let lang = this.getCurrentLanguage();
        return this.isRTL(lang);
    }

    getDirByLang(lang: ILanguage | string): string {
        return this.isRTL(lang) ? LanguageService.DIR_RTL : LanguageService.DIR_LTR
    }

    useLang(lang: ILanguage | string, langFromQuery?: string): void {
        let langCode = this._chewLangCode(lang);
        if (!langCode || !langCode.length) {
            throw new Error("Can't use specified language");
        }
        // use new language
        $('html').attr('lang', langCode);
        $('html').attr('dir', this.getDirByLang(lang));
        $('body').attr('dir', this.getDirByLang(lang));
        $('body').addClass(this.getDirByLang(lang));
        this.translate.setDefaultLang(langCode);
        this.translate.use(langCode);
        if (langFromQuery != langCode) {
            if(GenericConfig.isWebComponent) {
                this.wcLangCode = langCode;
            } else {
				// store new selected language
				this.cookie.set('lang', langCode, null, '/', this.restUrlsConf.ssoDomain, false, 'Lax');
			}
            // update current language into local storage
            this.updateCurrentLanguage(langCode);
        }
        // push subscribers
        this.languageSelected$.next( this.getCurrentLanguage() );
    }

    storeLangList(langs: Array<ILanguage>) {
        localStorage.setItem('langList', JSON.stringify(langs));
    }

    getStoredLangList(): Array<ILanguage> {
        const langList = localStorage.getItem('langList');
        return langList ? JSON.parse(langList) : null;
    }

    getStoredLangCodes(): Array<string> {
        return this.getStoredLangList().map(l => l.code);
    }

    feUrlByLangCode(langCode: string, currentUrl: string): string {
        let isDefaultLang = langCode == this.getDefaultLanguage();
        if (GenericConfig.useLangSubDomain) {
            let langCodePrefix = isDefaultLang ? '' : `${langCode}.`;
            const r = this.subDomainRegex();
            return location.hostname.match(r) ?
               `${location.protocol}//${location.hostname.replace(location.hostname.match(r)[0], langCodePrefix)}${currentUrl}` :
                `${location.protocol}//${langCodePrefix}${location.hostname}${currentUrl}`;
        } else {
            return isDefaultLang ?
                `${location.origin}/${currentUrl}` :
                location.origin.replace(location.origin, `${location.origin}/${langCode}${currentUrl == '/' ? '' : '/' + currentUrl}`);
        }
    }

    initLangList() {
        //get lang from query
        let langFromQuery = this.getLangCodeFromQuery();
        // get language list from the local storage
        let langList = this.getStoredLangList();
        if (langList && langList.length) {
            this.langList = LanguageDTO.createFromArray(langList);
            let lang = this.findCurrent(this.langList, langFromQuery);
            if(!GenericConfig.isWebComponent) {
               this.useLang(lang, langFromQuery);
               this.currLang = lang;
            }
            if(this.currLang) {
                this.currLangName = this.currLang.name;
            }
            this.langListSub.next(this.langList);
        }
        // get language list from the server
        else {
            this.getAllLanguages()
                .subscribe(
                    langs => {
                        this.langList = langs;
                        this.currLang = this.findCurrent(this.langList, langFromQuery);
                        if(this.currLang) {
                            this.currLangName = this.currLang.name;
                        }
                        // store in local storage the language list
                        this.storeLangList(langs);
                        if(!GenericConfig.isWebComponent) {
                          // use current language
                          this.useLang(this.currLang, langFromQuery);
                        }
                        this.langListSub.next(this.langList);
                    },
                    error => console.log(error)
                );
        }
    }

    getFeUrlByLangCode(currLang): string {
        let currentURL = window.location.href.substring(window.location.origin.length);
        if (currLang.code != this.getDefaultLanguage()) {
            currentURL = currentURL.replace(`/${currLang.code}/`, '/');
        }
        return currentURL;
    }

    langSelected(lang: ILanguage): void {
        try {
            let currLang = this.getCurrentLanguage();
            if(currLang.code !== lang.code) {
                FakeloaderComponent.start();
                let currentUrl = '';
                if (GenericConfig.redirectAfterChangeLang) {
                    currentUrl = this.getFeUrlByLangCode(currLang);
                }
                if(!GenericConfig.isMultiLanguages) {
                    this.useLang(lang);
                }
                console.log(`Language has switched to ${lang.origin}. Refreshing...`);
                const self = this;
                this.ngZone.runOutsideAngular(() => {
                    setTimeout(() => {
                        if(GenericConfig.isMultiLanguages) {
                            if(lang.domain) {
                                window.location.href = this.sanitizer.sanitize(SecurityContext.URL,`http://${lang.domain}${currentUrl}`);
                            } else {
                                window.location.href = this.sanitizer.sanitize(SecurityContext.URL,self.feUrlByLangCode(lang.code, currentUrl));
                            }
                        } else {
 			     if (lang.landing_page) {
                                const url = window.location.protocol + '//' + window.location.host + '/' + lang.landing_page;
                                window.location.href = this.sanitizer.sanitize(SecurityContext.URL, url);
                            } else if (currentUrl) { // from GenericConfig.redirectAfterChangeLang
                                const url = `${location.protocol}//${location.hostname}${currentUrl}`;
                                window.location.href = this.sanitizer.sanitize(SecurityContext.URL, url);
                            } else {
                                window.location.reload();
                            }
                        }
                        FakeloaderComponent.stop();
                    }, 300);
                });
            }
        } catch (e) {
            FakeloaderComponent.stop();
            console.log(e.message);
        }
    }

    getLangCodeFromQuery() {
        let langList = this.getStoredLangList();
        let parsedUrl = this.router.parseUrl(location.search);
        let found = langList ? langList.find(lang => lang.code === parsedUrl.queryParams.lang) : null;
        return found ? parsedUrl.queryParams.lang : '';
    }

    getLangByCode(langCode) {
        let langList = this.getStoredLangList();
        return langList.find(lang => lang.code === langCode);
    }
}
